import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react'
import { useCallback, useRef, useState } from 'react'

type Offset = { x: number; y: number }

type DragSession = {
  pointerId: number
  startX: number
  startY: number
  startOffset: Offset
  /** ドラッグ開始時点の要素位置からオフセットを除いた基準矩形 */
  baseRect: { left: number; top: number; right: number; bottom: number }
  dragging: boolean
}

/** タップとドラッグを区別する移動量 (px) */
const DRAG_THRESHOLD = 6

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const stopPropagation = (e: Event) => e.stopPropagation()

/**
 * 固定配置の要素をドラッグで移動できるようにするフック。
 * しきい値未満の移動はタップとして扱い、ドラッグ後のクリックは抑止する。
 * 親のネイティブ touch リスナー（スワイプバック等）に伝播させないよう、要素上の touch イベントは止める。
 */
export const useDragOffset = () => {
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 })
  const offsetRef = useRef<Offset>(offset)
  const sessionRef = useRef<DragSession | null>(null)
  const suppressClickRef = useRef(false)
  const nodeRef = useRef<HTMLElement | null>(null)

  const attach = useCallback((node: HTMLElement | null) => {
    if (nodeRef.current) {
      nodeRef.current.removeEventListener('touchstart', stopPropagation)
      nodeRef.current.removeEventListener('touchmove', stopPropagation)
      nodeRef.current.removeEventListener('touchend', stopPropagation)
    }
    nodeRef.current = node
    if (!node) return
    node.addEventListener('touchstart', stopPropagation, { passive: true })
    node.addEventListener('touchmove', stopPropagation, { passive: true })
    node.addEventListener('touchend', stopPropagation, { passive: true })
  }, [])

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    const current = offsetRef.current
    suppressClickRef.current = false
    sessionRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startOffset: current,
      baseRect: {
        left: rect.left - current.x,
        top: rect.top - current.y,
        right: rect.right - current.x,
        bottom: rect.bottom - current.y,
      },
      dragging: false,
    }
  }, [])

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const session = sessionRef.current
    if (!session || session.pointerId !== e.pointerId) return

    const dx = e.clientX - session.startX
    const dy = e.clientY - session.startY
    if (!session.dragging) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      session.dragging = true
      // ドラッグ確定後にキャプチャする（タップ時にキャプチャすると子ボタンの click が発火しなくなるため）
      e.currentTarget.setPointerCapture(e.pointerId)
    }

    const { baseRect, startOffset } = session
    const next = {
      x: clamp(startOffset.x + dx, -baseRect.left, window.innerWidth - baseRect.right),
      y: clamp(startOffset.y + dy, -baseRect.top, window.innerHeight - baseRect.bottom),
    }
    offsetRef.current = next
    setOffset(next)
  }, [])

  const endDrag = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const session = sessionRef.current
    if (!session || session.pointerId !== e.pointerId) return
    if (session.dragging) {
      suppressClickRef.current = true
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
    }
    sessionRef.current = null
  }, [])

  const onClickCapture = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    if (!suppressClickRef.current) return
    suppressClickRef.current = false
    e.preventDefault()
    e.stopPropagation()
  }, [])

  return {
    attach,
    offset,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
    },
  }
}
